import React, { useId } from "react";
import { AtSign, Camera, Trash2, UserRound } from "lucide-react";

export default function ProfileSection({
  profile,
  profilePreview,
  profileError,
  profileSuccess,
  profileLoading,
  canEditUsername = true,
  onChange,
  onPicChange,
  onSubmit,
}) {
  const uploadInputId = useId();
  const inputShellClassName = "rounded-2xl border border-slate-200 bg-white/90 px-3 py-2.5 shadow-sm transition focus-within:border-slate-400 focus-within:shadow-[0_0_0_4px_rgba(148,163,184,0.1)]";
  const inputClassName = "mt-0.5 w-full border-none bg-transparent p-0 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none";

  return (
    <form className="flex flex-col gap-3 px-5 pb-5" onSubmit={onSubmit} autoComplete="off" noValidate>
      <div className={inputShellClassName}>
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <UserRound className="h-4.5 w-4.5" />
          </span>
          <div className="min-w-0 flex-1">
            <label htmlFor="profile-username" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Username
            </label>
            <input
              id="profile-username"
              type="text"
              name="username"
              placeholder="Enter username"
              className={`${inputClassName} ${!canEditUsername ? "cursor-not-allowed text-slate-500" : ""}`}
              value={profile.username}
              onChange={onChange}
              readOnly={!canEditUsername}
            />
            {!canEditUsername ? (
              <p className="mt-1 text-[11px] text-slate-400">
                Username can only be changed by admin accounts.
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <div className={inputShellClassName}>
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <AtSign className="h-4.5 w-4.5" />
          </span>
          <div className="min-w-0 flex-1">
            <label htmlFor="profile-email" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Email Address
            </label>
            <input
              id="profile-email"
              type="text"
              name="email"
              placeholder="Enter email address"
              className={inputClassName}
              value={profile.email}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,0.96),_rgba(248,250,252,0.92))] p-3.5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative mx-auto sm:mx-0">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
              {profilePreview ? (
                <img
                  src={profilePreview}
                  alt="Profile preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
                  <UserRound className="h-10 w-10" />
                </div>
              )}
            </div>
            {profilePreview ? (
              <button
                type="button"
                onClick={() => onPicChange(null)}
                className="absolute -right-2 -top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/80 bg-white text-slate-500 shadow-lg shadow-slate-200/80 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none"
                aria-label="Delete profile picture"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-2">
              <p className="text-sm font-semibold text-slate-800">Profile Picture</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Upload a clear square image for a cleaner account identity.
              </p>
            </div>

            <input
              id={uploadInputId}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onPicChange(file);
              }}
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label
                htmlFor={uploadInputId}
                className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-[0_10px_20px_rgba(15,23,42,0.14)] transition hover:bg-slate-800"
              >
                <Camera className="h-4 w-4" />
                Choose Image
              </label>
              <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                <span className="block truncate">
                  {profile.picture?.name || "No file selected"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {profileError && <div className="text-xs text-red-500 mt-1">{profileError}</div>}
      {profileSuccess && <div className="text-xs text-emerald-600 mt-1">{profileSuccess}</div>}
      <button
        type="submit"
        disabled={profileLoading}
        className="mt-2 rounded-xl px-5 py-2.5 text-base font-semibold text-white shadow-[0_12px_24px_rgba(15,23,42,0.16)] transition hover:-translate-y-0.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        style={{ background: "var(--color-primary)" }}
      >
        {profileLoading ? "Saving..." : "Save Profile"}
      </button>
    </form>
  );
}
